// src/validations/schemas.js

import * as yup from 'yup';
import * as Yup from 'yup';
import { t } from 'i18next';

// Common validation patterns
const phoneRegex = /^\d{10}$/;
const passwordRegex = /^[a-zA-Z0-9\s]{8,}$/; //   Added \s for spaces

//   UPDATED: Allow spaces in username pattern
const usernamePattern = /^[\u0600-\u06FFa-zA-Z0-9_\s]+$/;

//   UPDATED: Allow spaces in password pattern
const passwordPattern = /^[a-zA-Z0-9\s]+$/;

// Schema for the doctor registration form
export const doctorSchema = Yup.object({
    name: Yup.string()
        .min(3, 'Name must be at least 3 characters')
        .required('Name is required'),
    specialization: Yup.string()
        .min(3, 'Specialization must be at least 3 characters')
        .required('Specialization is required'),
    contactNumber: Yup.string()
        .matches(/^[0-9]{10}$/, 'Must be a valid Egyptian phone number')
        .required('Contact number is required'),
});

// Schema for the patient form
export const patientSchema = Yup.object({
    name: Yup.string()
        .min(3, 'Name must be at least 3 characters')
        .required('Name is required'),
    age: Yup.number()
        .positive('Age must be a positive number')
        .integer('Age must be an integer')
        .min(0, 'Age cannot be negative')
        .max(120, 'Age seems unrealistic')
        .required('Age is required'),
    gender: Yup.string()
        .oneOf(['male', 'female', 'other'], 'Invalid gender')
        .required('Gender is required'),
    phoneNumber: Yup.string()
        .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
        .required('Phone number is required'),
    socialNumber: Yup.string().optional(),
    address: Yup.string().optional(),
    doctorReferred: yup.object().nullable().shape({
        _id: yup.string().required(),
    }),
});

// Radiologist schemas
export const radiologistSchema = yup.object({
    name: yup.string().required('Name is required'),
    gender: yup.string()
        .oneOf(['male', 'female', 'other'], 'Please select a valid gender')
        .required('Gender is required'),
    age: yup.number()
        .integer('Age must be a whole number')
        .min(18, 'Age must be at least 18')
        .max(150, 'Age cannot exceed 150')
        .required('Age is required'),
    phoneNumber: yup
        .string()
        .matches(phoneRegex, 'Please enter a valid 10-digit phone number')
        .required('Phone number is required'),
    licenseId: yup.string()
        .min(5, 'License ID must be at least 5 characters long')
        .max(20, 'License ID cannot exceed 20 characters')
        .optional(),
    isActive: yup.boolean().default(true),
});

// Radiologist Technician schemas
export const radiologyTechnicianSchema = yup.object({
    name: yup.string()
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name cannot exceed 100 characters')
        .required('Name is required'),
    age: yup.number()
        .integer('Age must be a whole number')
        .min(18, 'Age must be at least 18')
        .max(100, 'Age cannot exceed 100')
        .required('Age is required'),
});

// Appointment schemas
export const appointmentSchema = yup.object({
    patientId: yup.string().required('Patient is required'),
    doctorId: yup.string().required('Doctor is required'),
    date: yup.date().required('Date is required').min(new Date(), 'Date cannot be in the past'),
    time: yup.string().required('Time is required'),
    type: yup
        .string()
        .oneOf(['xray', 'ct', 'mri', 'ultrasound'], 'Invalid scan type')
        .required('Scan type is required'),
    priority: yup
        .string()
        .oneOf(['routine', 'urgent', 'emergency'], 'Invalid priority level')
        .default('routine'),
    notes: yup.string(),
});

// Stock schemas
export const stockSchema = Yup.object().shape({
    name: Yup.string().required('Stock name is required'),
    quantities: Yup.object()
        .test('at-least-one', 'At least one branch quantity is required', value => {
            return value && Object.values(value).some(v => v !== undefined && v !== null && v !== '');
        })
        .required('Quantities are required'),
    minimumThreshold: Yup.number().min(0, 'Minimum threshold cannot be negative').required('Minimum threshold is required'),
    price: Yup.number().min(0, 'Price cannot be negative').required('Price is required'),
    validUntil: Yup.date().required('Valid until date is required'),
});

// Scan schemas
export const scanSchema = yup.object({
    patientId: yup.string().required('Patient is required'),
    doctorId: yup.string().required('Doctor is required'),
    categoryId: yup.string().required('Scan category is required'),
    date: yup.date().required('Date is required'),
    status: yup
        .string()
        .oneOf(['scheduled', 'in-progress', 'completed', 'cancelled'], 'Invalid status')
        .default('scheduled'),
    notes: yup.string(),
    findings: yup.string(),
    attachments: yup.array().of(yup.string()),
});

// Scan Category schemas
export const scanCategorySchema = yup.object({
    name: yup.string().required('Category name is required'),
    description: yup.string(),
    price: yup.number().min(0, 'Price cannot be negative').required('Price is required'),
    duration: yup.number().min(1, 'Duration must be at least 1 minute').required('Duration is required'),
    isActive: yup.boolean().default(true),
});

// Patient History schemas
export const patientHistorySchema = yup.object({
    patientId: yup.string().required('Patient is required'),
    doctorId: yup.string().required('Doctor is required'),
    date: yup.date().required('Date is required'),
    diagnosis: yup.string().required('Diagnosis is required'),
    treatment: yup.string().required('Treatment is required'),
    notes: yup.string(),
});

//   UPDATED: Register Schema with spaces allowed
export const registerSchema = Yup.object().shape({
    username: Yup.string()
        .required('Username is required')
        .min(3, 'Username must be at least 3 characters long')
        .max(30, 'Username cannot exceed 30 characters')
        .matches(
            usernamePattern,
            'Username can only contain Arabic letters, English letters, numbers, underscores, and spaces'
        )
        .test(
            'no-leading-trailing-spaces',
            'Username cannot start or end with spaces',
            value => value ? value === value.trim() : true
        ),

    name: Yup.string()
        .required('Name is required')
        .min(2, 'Name must be at least 2 characters long')
        .max(100, 'Name cannot exceed 100 characters')
        .test(
            'no-leading-trailing-spaces',
            'Name cannot start or end with spaces',
            value => value ? value === value.trim() : true
        ),

    email: Yup.string()
        .email('Please provide a valid email address')
        .nullable()
        .notRequired(),

    password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters long')
        .matches(
            passwordPattern,
            'Password must contain only letters (a-z, A-Z), numbers (0-9), or spaces'
        )
        .test(
            'no-leading-trailing-spaces',
            'Password cannot start or end with spaces',
            value => value ? value === value.trim() : true
        ),

    confirmPassword: Yup.string()
        .required('Please confirm your password')
        .oneOf([Yup.ref('password'), null], 'Passwords do not match'),

    role: Yup.string()
        .oneOf(
            ['receptionist', 'superAdmin', 'radiologist', 'representative'],
            'Role must be one of: receptionist, superAdmin, radiologist'
        )
        .default('receptionist'),

    phone: Yup.string()
        .nullable()
        .notRequired()
        .matches(
            /^[+]?[\d\s-]*$/,
            'Invalid phone number format'
        ),

    branch: Yup.string()
        .nullable()
        .notRequired()
        .test(
            'valid-branch-id',
            'Invalid branch ID format',
            value => {
                if (!value || value === '') return true;
                return /^[0-9a-fA-F]{24}$/.test(value);
            }
        )
});

//   UPDATED: Login Schema with spaces allowed
export const loginSchema = Yup.object().shape({
    username: Yup.string()
        .required('Username is required')
        .test(
            'no-leading-trailing-spaces',
            'Username cannot start or end with spaces',
            value => value ? value === value.trim() : true
        ),
    password: Yup.string()
        .required('Password is required')
        .test(
            'no-leading-trailing-spaces',
            'Password cannot start or end with spaces',
            value => value ? value === value.trim() : true
        )
});

//   UPDATED: Change Password Schema with spaces allowed
export const changePasswordSchema = Yup.object().shape({
    currentPassword: Yup.string()
        .required('Current password is required'),
    newPassword: Yup.string()
        .required('New password is required')
        .min(8, 'Password must be at least 8 characters long')
        .matches(
            passwordPattern,
            'Password must contain only letters (a-z, A-Z), numbers (0-9), or spaces'
        )
        .test(
            'no-leading-trailing-spaces',
            'Password cannot start or end with spaces',
            value => value ? value === value.trim() : true
        )
        .notOneOf(
            [Yup.ref('currentPassword')],
            'New password must be different from current password'
        ),
    confirmPassword: Yup.string()
        .required('Please confirm your new password')
        .oneOf([Yup.ref('newPassword'), null], 'Passwords do not match')
});

//   UPDATED: Update Profile Schema with spaces allowed
export const updateProfileSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, 'Name must be at least 2 characters long')
        .max(100, 'Name cannot exceed 100 characters')
        .test(
            'no-leading-trailing-spaces',
            'Name cannot start or end with spaces',
            value => value ? value === value.trim() : true
        ),
    username: Yup.string()
        .min(3, 'Username must be at least 3 characters long')
        .max(30, 'Username cannot exceed 30 characters')
        .matches(
            usernamePattern,
            'Username can only contain Arabic letters, English letters, numbers, underscores, and spaces'
        )
        .test(
            'no-leading-trailing-spaces',
            'Username cannot start or end with spaces',
            value => value ? value === value.trim() : true
        ),
    email: Yup.string()
        .email('Please provide a valid email address')
        .nullable(),
    phone: Yup.string()
        .nullable()
        .matches(
            /^[+]?[\d\s-]*$/,
            'Invalid phone number format'
        )
});

// Forgot Password Schema
export const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
        .email(t('validation.email'))
        .required(t('validation.required')),
});

//   UPDATED: Reset Password Schema with spaces allowed
export const resetPasswordSchema = Yup.object().shape({
    password: Yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
            passwordPattern,
            'Password must contain only letters (a-z, A-Z), numbers (0-9), or spaces'
        )
        .test(
            'no-leading-trailing-spaces',
            'Password cannot start or end with spaces',
            value => value ? value === value.trim() : true
        )
        .required(t('validation.required')),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], t('validation.passwordsMustMatch'))
        .when('password', {
            is: (val) => val && val.length > 0,
            then: (schema) => schema.required(t('validation.required')),
        })
});

// Create Branch Schema
export const createBranchSchema = Yup.object({
    name: Yup.string()
        .required(t('validation.required', { field: t('branches.name') }))
        .min(3, t('validation.minLength', { field: t('branches.name'), min: 3 }))
        .max(100, t('validation.maxLength', { field: t('branches.name'), max: 100 })),
    location: Yup.string()
        .required(t('validation.required', { field: t('branches.location') }))
        .min(5, t('validation.minLength', { field: t('branches.location'), min: 5 }))
        .max(200, t('validation.maxLength', { field: t('branches.location'), max: 200 })),
    address: Yup.string()
        .required(t('validation.required', { field: t('branches.address') }))
        .max(500, t('validation.maxLength', { field: t('branches.address'), max: 500 })),
    phone: Yup.string()
        .required(t('validation.required', { field: t('branches.phone') }))
        .matches(/^\d{10}$/, t('validation.phoneNumber')),
    manager: Yup.string()
        .required(t('validation.required', { field: t('branches.manager') }))
        .min(2, t('validation.minLength', { field: t('branches.manager'), min: 2 }))
        .max(100, t('validation.maxLength', { field: t('branches.manager'), max: 100 })),
});

// Export default object with all schemas
export default {
    doctorSchema,
    patientSchema,
    radiologistSchema,
    radiologyTechnicianSchema,
    appointmentSchema,
    stockSchema,
    scanSchema,
    scanCategorySchema,
    patientHistorySchema,
    registerSchema,
    loginSchema,
    changePasswordSchema,
    updateProfileSchema,
    forgotPasswordSchema,
    resetPasswordSchema,
    createBranchSchema,
};